/**
 * Brand URL verification, shared by the three forms that do it.
 *
 * Add Project, Competitor Spy and Affiliate Gap all post to the same endpoint
 * and all handled the response slightly differently. This is the one copy.
 *
 * The backend keeps a cache (verified_brand_urls): a URL that has been verified
 * once answers instantly for everyone afterwards, without the Zyte render or
 * the Claude call. `cached` on the response says which happened.
 */

const API_BASE = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

/**
 * Gating is by email, as everywhere else in this product — users.role is not
 * maintained, so a role check would lock out the one account that has access.
 */
export const ADMIN_EMAIL = 'mars.mediacle@gmail.com';

export const canOverrideVerification = (user) => user?.email === ADMIN_EMAIL;

/** https:// on the front, because the endpoint needs an absolute URL. */
export const normalizeUrlForVerification = (url) => {
  const trimmed = String(url || '').trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const post = async (path, body) => {
  const response = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  return response.json();
};

/**
 * Ask whether a URL is a gambling operator.
 *
 * Resolves to { verified, message, verticals, cached } and never rejects — the
 * three callers all treated a thrown error as "not verified" anyway, and one
 * shape is easier to render than a try/catch in each of them.
 */
export const verifyBrandUrl = async (url) => {
  try {
    const data = await post('/api/brandAddition/verify-url', {
      url: normalizeUrlForVerification(url)
    });

    if (data.success && data.isGamblingBrand) {
      return {
        verified: true,
        verticals: data.verticals || [],
        cached: data.cached === true,
        source: data.source || 'ai',
        message: data.message || `Verified! Detected: ${(data.verticals || []).join(', ') || 'Gambling Brand'}`
      };
    }

    return {
      verified: false,
      verticals: [],
      cached: false,
      message: data.message || 'This URL does not appear to be a gambling/betting website.'
    };
  } catch (error) {
    console.error('URL verification error:', error);
    return {
      verified: false,
      verticals: [],
      cached: false,
      message: 'Failed to verify URL. Please try again.'
    };
  }
};

/**
 * The Trading & Investment counterpart: ask whether a URL is a forex/CFD broker.
 *
 * Same resolved shape as verifyBrandUrl so the Add Project form can call either
 * one and render the answer through one code path. `verticals` here carries the
 * instruments the broker offers (CFDs, Shares, ETFs...), which the form uses to
 * pre-select the sub-category picker — exactly as the gambling check pre-selects
 * verticals.
 *
 * The backend fails OPEN on a fetch or Claude failure (verified: true with
 * failedOpen). A verification outage must not stop a customer adding a brand;
 * the classifier still judges every referral domain afterwards.
 */
export const verifyTradingBrandUrl = async (url) => {
  try {
    const data = await post('/api/forexaddbrand/verify-url', {
      url: normalizeUrlForVerification(url)
    });

    if (data.success && data.isBroker) {
      return {
        verified: true,
        verticals: data.subVerticals || [],
        cached: data.cached === true,
        source: data.source || 'ai',
        message: data.message || 'Verified as a trading brand.'
      };
    }

    return {
      verified: false,
      verticals: [],
      cached: false,
      message: data.message || 'This URL does not appear to be a forex/CFD broker or trading platform.'
    };
  } catch (error) {
    console.error('Trading URL verification error:', error);
    return {
      verified: false,
      verticals: [],
      cached: false,
      message: 'Failed to verify URL. Please try again.'
    };
  }
};

/**
 * Does this account have the Trading & Investment vertical? Answers false on any
 * failure, which is the safe direction: the form then behaves exactly as it did
 * before this feature existed.
 */
export const fetchTradingAccess = async () => {
  try {
    const response = await fetch(`${API_BASE}/api/forexaddbrand/access`, {
      credentials: 'include'
    });
    const data = await response.json();
    return {
      allowed: data?.allowed === true,
      subVerticals: data?.subVerticals || []
    };
  } catch (error) {
    return { allowed: false, subVerticals: [] };
  }
};

/**
 * Admin only: state that a URL is a brand and have it remembered.
 *
 * For the cases the classifier gets wrong — a new operator with a thin landing
 * page, a site that geo-blocks the fetch, a brand rendered entirely in
 * JavaScript. The domain lands in the same cache the ordinary check reads, so
 * it is verified everywhere from then on, not just in the form it was typed in.
 */
export const overrideBrandUrl = async (url, verticals = []) => {
  try {
    const data = await post('/api/brandAddition/verify-url/override', {
      url: normalizeUrlForVerification(url),
      verticals
    });

    return {
      verified: data.success === true,
      verticals: data.verticals || [],
      cached: false,
      source: 'admin_override',
      message: data.message || (data.success ? 'Marked as verified.' : 'Could not save the override.')
    };
  } catch (error) {
    console.error('URL override error:', error);
    return {
      verified: false,
      verticals: [],
      cached: false,
      message: 'Could not save the override. Please try again.'
    };
  }
};
